import { useState, useRef, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

const translations = {
  en: {
    title: "Upload Resume (PDF)",
    dropZone: "Drop PDF here",
    dropZoneActive: "Release the file",
    dropZoneClick: "or click to select",
    placeholder: "Cleaned text from PDF will appear here...",
    close: "Close",
    insert: "Insert into chat",
    processing: "Processing PDF... This may take a few seconds",
    errorFile: "Please upload a PDF file",
    errorProcessing:
      "Error processing PDF. The file may be password-protected or corrupted.",
    noText: "(No text found in PDF)",
  },
  fr: {
    title: "Téléchargez votre CV (PDF)",
    dropZone: "Glissez-déposez le PDF ici",
    dropZoneActive: "Relâchez le fichier",
    dropZoneClick: "ou cliquez pour sélectionner",
    placeholder: "Le texte nettoyé du PDF apparaîtra ici...",
    close: "Fermer",
    insert: "Insérer dans le chat",
    processing: "Traitement du PDF en cours... Cela peut prendre quelques secondes",
    errorFile: "Veuillez télécharger un fichier PDF",
    errorProcessing:
      "Erreur lors du traitement du PDF. Le fichier peut être protégé par mot de passe ou corrompu.",
    noText: "(Aucun texte trouvé dans le PDF)",
  },
};

const lang = "en";
const t = translations[lang];

export default function PdfToTextModal({ onClose, onApply }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

  const removeContactsAndArtifacts = (rawText) => {
    let cleaned = rawText;

    cleaned = cleaned.replace(
      /^(Page|Страница|Сторінка|Seite|Página|Lapa|Pagina|Strana|Page \d+ of \d+|Page \d+\/\d+|\d+ of \d+|\d+\/\d+|\d+\s*\/\s*\d+).*$/gim,
      ""
    );
    cleaned = cleaned.replace(/^\s*\d+\s*$/gim, "");

    const lines = cleaned.split("\n");
    const filteredLines = [];
    let repeatCount = 0;
    let lastLine = "";

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed && trimmed === lastLine) {
        repeatCount++;
        if (repeatCount < 3) filteredLines.push(line);
      } else {
        repeatCount = 1;
        lastLine = trimmed;
        filteredLines.push(line);
      }
    }
    cleaned = filteredLines.join("\n");

    cleaned = cleaned
      .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "")
      .replace(/\+?\d[\d\s().-]{7,}\d/g, "")
      .replace(/\bhttps?:\/\/\S+/gi, "")
      .replace(/\bwww\.\S+/gi, "")
      .replace(/\blinkedin\.com\/\S*/gi, "")
      .replace(/\b(Contact|Contacts|Email|E-mail|LinkedIn|Phone|Телефон|Почта|Контакты)\b/gi, "");

    return cleaned.replace(/[ \t]{2,}/g, " ").replace(/\n{3,}/g, "\n\n").trim();
  };

  const processFile = async (file) => {
    if (!file || file.type !== "application/pdf") {
      setText(t.errorFile);
      return;
    }

    setLoading(true);
    setText("");

    try {
      const buffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

      let fullText = "";
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        fullText += content.items.map((item) => item.str).join(" ") + "\n\n";
      }

      const cleaned = removeContactsAndArtifacts(fullText);
      setText(cleaned || t.noText);
    } catch (err) {
      console.error(err);
      setText(t.errorProcessing);
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleClick = () => fileInputRef.current?.click();

  return (
    <div style={overlay(isDark)} onClick={onClose}>
      <div style={modal(isDark, isMobile)} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle(isDark, isMobile)}>{t.title}</h2>

        <div
          style={{
            ...dropZoneBase(isDark, isMobile),
            borderColor: dragActive ? "#a78bfa" : isDark ? "#444" : "#ccc",
            background: dragActive
              ? isDark
                ? "#a78bfa22"
                : "#a78bfa12"
              : isDark
              ? "#1e1e1e"
              : "#f9f9f9",
            transform: dragActive ? "scale(1.02)" : "scale(1)",
            boxShadow: dragActive
              ? "0 12px 30px rgba(167, 139, 250, 0.2)"
              : isDark
              ? "0 4px 12px rgba(0,0,0,0.3)"
              : "0 4px 12px rgba(0,0,0,0.08)",
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <div style={{ textAlign: "center", pointerEvents: "none" }}>
            <div style={{ fontSize: isMobile ? "40px" : "56px", marginBottom: "16px", opacity: 0.7 }}>📄</div>
            <p style={{ margin: "0 0 8px 0", fontSize: isMobile ? "15px" : "17px", fontWeight: "500" }}>
              {dragActive ? t.dropZoneActive : t.dropZone}
            </p>
            <p style={{ margin: 0, fontSize: isMobile ? "12px" : "14px", opacity: 0.7 }}>
              {t.dropZoneClick}
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={(e) => e.target.files?.[0] && processFile(e.target.files[0])}
          style={{ display: "none" }}
        />

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t.placeholder}
          style={textAreaStyle(isDark, isMobile)}
          disabled={loading}
        />

        <div style={{ display: "flex", gap: isMobile ? 12 : 16, flexDirection: isMobile ? "column-reverse" : "row", justifyContent: "flex-end", marginTop: 28 }}>
        <button
            style={btnClose(isDark, isMobile)}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(248, 113, 113, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(248, 113, 113, 0.4)";
            }}
          >
            {t.close}
          </button>
          <button
            style={{
              ...btnPrimary(isDark, isMobile),
              opacity: !text.trim() || loading ? 0.5 : 1,
              cursor: !text.trim() || loading ? "not-allowed" : "pointer",
            }}
            onClick={() => onApply(text)}
            disabled={!text.trim() || loading}
          >
            {loading ? "Processing..." : t.insert}
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: "center", marginTop: 20, color: "#a78bfa", fontSize: "15px" }}>
            ⏳ {t.processing}
          </div>
        )}
      </div>
    </div>
  );
}

// Мобильные стили
const overlay = (isDark) => ({
  position: "fixed",
  inset: 0,
  background: isDark ? "rgba(0, 0, 0, 0.88)" : "rgba(0, 0, 0, 0.65)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10000,
  backdropFilter: "blur(10px)",
});

const modal = (isDark, isMobile) => ({
  background: isDark ? "#111" : "#ffffff",
  color: isDark ? "#eee" : "#222",
  padding: isMobile ? "24px" : "48px",
  borderRadius: "28px",
  width: "95%",
  maxWidth: "960px",
  maxHeight: "90vh",
  boxShadow: isDark
    ? "0 20px 60px rgba(0,0,0,0.8)"
    : "0 20px 60px rgba(0,0,0,0.18)",
  overflowY: "auto",
  transition: "all 0.3s ease",
});

const titleStyle = (isDark, isMobile) => ({
  margin: isMobile ? "0 0 24px 0" : "0 0 36px 0",
  fontSize: isMobile ? "22px" : "28px",
  fontWeight: "600",
  textAlign: "center",
  color: isDark ? "#ddd" : "#333",
});

const dropZoneBase = (isDark, isMobile) => ({
  border: `2px dashed ${isDark ? "#444" : "#ccc"}`,
  borderRadius: "24px",
  padding: isMobile ? "32px 16px" : "64px 32px",
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.35s ease",
  marginBottom: isMobile ? "24px" : "36px",
  boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.08)",
});

const textAreaStyle = (isDark, isMobile) => ({
  width: "100%",
  height: isMobile ? "260px" : "420px",
  padding: isMobile ? "16px" : "24px",
  borderRadius: "18px",
  border: `1px solid ${isDark ? "#333" : "#e0e0e0"}`,
  background: isDark ? "#1a1a1a" : "#fcfcfc",
  color: isDark ? "#eee" : "#333",
  fontSize: isMobile ? "14px" : "16px",
  fontFamily: "inherit",
  lineHeight: "1.6",
  resize: "vertical",
  outline: "none",
  transition: "all 0.3s ease",
});

const btnPrimary = (isDark, isMobile) => ({
  padding: isMobile ? "14px 28px" : "18px 40px",
  background: "linear-gradient(135deg, #a78bfa 0%, #818cf8 100%)",
  color: "#fff",
  border: "none",
  borderRadius: "16px",
  fontSize: isMobile ? "16px" : "18px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "all 0.3s ease",
  boxShadow: "0 8px 20px rgba(167, 139, 250, 0.25)",
});

const btnClose = (isDark, isMobile) => ({
  padding: isMobile ? "12px 24px" : "14px 28px",  // чуть меньше
  background: "#f87171",                          // насыщенный красный
  color: "#fff",
  border: "none",
  borderRadius: "14px",
  fontSize: isMobile ? "14px" : "16px",           // чуть меньше текста
  fontWeight: "600",
  cursor: "pointer",
  transition: "all 0.3s ease",
  boxShadow: "0 4px 12px rgba(248, 113, 113, 0.4)",
});

